import { useState } from "react";

/**
 * Reusable accordion component with customizable title and content
 * @param {string} title - Title text to display
 * @param {React.Component} children - Component to render when accordion is open
 * @param {boolean} defaultOpen - Whether accordion should be open by default
 * @param {string} className - Additional CSS classes
 * @param {boolean} hasRequiredActions - Whether there are required actions inside the accordion
 * @param {boolean} allActionsComplete - Whether all required actions are complete (green style)
 * @param {boolean} isWaiting - Whether waiting for other party to take action (orange style)
 * @param {boolean} isBlocked - Whether the section is blocked/disabled (gray style)
 */
function AccordionEditable({ 
  title, 
  children, 
  defaultOpen = false, 
  className = "",
  hasRequiredActions = false,
  allActionsComplete = false,
  isWaiting = false,
  isBlocked = false
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  // Determine styling based on state (priority: Blocked > Attention Needed > Waiting > Complete)
  const showBlocked = isBlocked;
  const hasRequired = hasRequiredActions && !allActionsComplete && !isWaiting && !isBlocked;
  const showWaiting = isWaiting && !hasRequiredActions && !isBlocked;
  const isComplete = allActionsComplete && !hasRequiredActions && !isWaiting && !isBlocked;

  // New status badge configuration (left side of header)
  const getStatusBadgeConfig = () => {
    if (showBlocked) {
      return { 
        label: "Blocked", 
        icon: "timer_blocked.svg",
        className: "accordion-status-badge-blocked"
      };
    }
    if (hasRequired) {
      return { 
        label: "Attention", 
        icon: "danger_filled.svg",
        className: "accordion-status-badge-attention"
      };
    }
    if (showWaiting) {
      return { 
        label: "Waiting", 
        icon: "timer_waiting.svg",
        className: "accordion-status-badge-waiting"
      };
    }
    if (isComplete) {
      return { 
        label: "Complete", 
        icon: "award_star.svg",
        className: "accordion-status-badge-complete"
      };
    }
    return null;
  };

  const statusBadge = getStatusBadgeConfig();

  return (
    <div 
      className={`accordion-editable ${isOpen ? 'open' : ''} ${className}`}
    >
      <div 
        className={`accordion-header ${isOpen ? 'open' : ''} ${showBlocked ? 'is-blocked' : ''} ${hasRequired ? 'has-required-actions' : ''} ${showWaiting ? 'is-waiting' : ''} ${isComplete ? 'all-actions-complete' : ''}`}
        onClick={toggleAccordion}
      >
        {/* New Status Badge - first element on left */}
        {statusBadge && (
          <div className={`accordion-status-badge ${statusBadge.className}`}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/icons/${statusBadge.icon}`}
              alt={statusBadge.label}
              className="accordion-status-badge-icon"
            />
            <span className="accordion-status-badge-text">{statusBadge.label}</span>
          </div>
        )}
        
        <div className="accordion-left">
          <span className="accordion-title">
            {title}
          </span>
        </div>
        <div className="accordion-right">
          <img 
            src={isOpen 
              ? `${process.env.PUBLIC_URL}/images/icons/arrow_down_blue_medium.svg` 
              : `${process.env.PUBLIC_URL}/images/icons/arrow_down_blue_light.svg`
            }
            alt={isOpen ? "Collapse" : "Expand"}
            className={`accordion-arrow-icon ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>
      
      <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export default AccordionEditable;
